import { useEffect, useMemo, useState, useCallback } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { format } from 'date-fns';
import { supabase } from '../../../lib/supabase';
import { useAuth } from '../../../hooks/useAuth';
import useIsMobile from '../../../hooks/useIsMobile';
import NewRunWizard from './NewRunWizard';
import ResyncModal from './ResyncModal';
import RemittanceModal from './RemittanceModal';
import BackfillModal from './BackfillModal';
import RunList, { needsALook, weekLabel } from './RunList';
import RunPanel, { RUN_TABS } from './RunPanel';
import LinePanel from './LinePanel';
import { DeleteRunDialog, IcnDialog, SendToGammisDialog } from './RunDialogs';
import useFiling from './useFiling';
import useRunMarks from './useRunMarks';
import {
  loadRuns as fetchRuns, loadRunLines,
  saveLineEdit as writeLineEdit, setLineStatus, removeLine, deleteRun as removeRun, recordClaimReference,
} from '../../../lib/billing/runs';
import { parse835 } from '../../../lib/edi/x835';
import { indexEobCodes } from '../../../lib/edi/eobCodes';
import './billing.css';

// Units on an exported claim are `claim_lines.units` and nothing else.
//
// That column is the SV104 value: for nursing it holds 15-minute units
// (hours x 4, which is how GAMMIS bills S9123 and S9122 — remittance 13512807
// carries 25 units on a single day, which cannot be hours), and for DME it
// holds the item count. How it got there is decided per service by
// service_catalog.unit_basis when the line is built.
//
// There used to be a per-run toggle here that recomputed units from `hours` at
// export time, defaulting to whole hours. Every one of the 33 runs is on that
// default, so both the CSV and the 837P have always emitted a quarter of the
// units GAMMIS expects. The toggle is gone; billing_runs.unit_basis is left in
// the database as a record of what past exports claimed.

const TAB_KEY = 'caera.runTab';
const readTab = () => { try { const t = sessionStorage.getItem(TAB_KEY); return RUN_TABS.includes(t) ? t : 'lines'; } catch { return 'lines'; } };

// Characters PostgREST's or() filter would read as syntax, not text.
const safeTerm = (q) => q.replace(/[,()*%\\]/g, ' ').trim();

// ── Component ─────────────────────────────────────────────────────────────────
// Claims → Runs: the runs list beside the run you are reading (option C,
// chosen 2026-10-20). Each run has its own address,
// /finance/claims/runs/<id>, and ?full=1 shows it full width without the list.
// The rules and writes are lib/billing/; this holds the screen's state.
export default function BillingRuns() {
  const { profile } = useAuth();
  const isMobile = useIsMobile();
  const navigate = useNavigate();
  const { runId: runIdParam } = useParams();
  const [params] = useSearchParams();
  const full = params.get('full') === '1' && !isMobile;

  const [runs, setRuns]           = useState([]);
  const [loading, setLoading]     = useState(true);
  const [lines, setLines]         = useState([]);
  const [linesLoading, setLinesLoading] = useState(false);
  const [tab, setTabState]        = useState(readTab);
  const [search, setSearch]       = useState('');
  const [lineHits, setLineHits]   = useState(null);   // run ids whose lines match the search
  const [searching, setSearching] = useState(false);
  const [filter, setFilter]       = useState('all');
  const [openLine, setOpenLine]   = useState(null);

  const [showWizard, setShowWizard] = useState(false);
  const [icnModal,   setIcnModal]   = useState(null);
  const [icnValue,   setIcnValue]   = useState('');
  const [icnSaving,  setIcnSaving]  = useState(false);
  const [eraModal,   setEraModal]   = useState(null);
  const [sortKey, setSortKey] = useState('service_date');
  const [sortAsc, setSortAsc] = useState(true);
  const [backfillOpen, setBackfillOpen] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [deleting,      setDeleting]      = useState(false);
  const [deleteError,   setDeleteError]   = useState('');
  const [resyncRun, setResyncRun] = useState(null);
  // Explanations for the codes on a remittance. Reference data, loaded once.
  const [eobIndex, setEobIndex] = useState({ eob: {}, carc: {}, rarc: {} });

  const loadRuns = useCallback(async () => {
    setLoading(true);
    setRuns(await fetchRuns(supabase));
    setLoading(false);
  }, []);
  useEffect(() => { loadRuns(); }, [loadRuns]);
  useEffect(() => {
    supabase.from('eob_codes').select('kind, code, description')
      .then(({ data }) => setEobIndex(indexEobCodes(data || [])));
  }, []);

  // The selected run: the one in the address, else (on a wide screen) the newest.
  const selected = useMemo(() => {
    if (runIdParam) return runs.find(r => r.id === runIdParam) || null;
    return isMobile ? null : runs[0] || null;
  }, [runs, runIdParam, isMobile]);

  const loadLines = useCallback(async (runId) => {
    setLinesLoading(true);
    setLines(await loadRunLines(supabase, runId));
    setLinesLoading(false);
  }, []);

  const {
    identity, settings, transfers, pendingRemits, transport, exportProblems, setExportProblems,
    submissions, submitting, ackFileRef, chooseAckFor, loadSubmissions, loadPendingRemittances,
    downloadEdi, exportGAMMIS, exportEDI, markSent, transportConfigured,
    requestSend, sendConfirm, setSendConfirm, confirmSend,
    askClaimStatus, refreshFromGammis, handleAckFile,
  } = useFiling({ runs, activeRun: selected, profile, loadRuns, loadLines });

  const { marks, recheck } = useRunMarks(runs, selected);

  useEffect(() => {
    setOpenLine(null);
    if (!selected) { setLines([]); return; }
    loadLines(selected.id);
    loadSubmissions(selected.id);
  }, [selected?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── where we are ──────────────────────────────────────────────────────────
  const go = useCallback((runId, { fullWidth = full, replace = false } = {}) => {
    const path = runId ? `/finance/claims/runs/${runId}` : '/finance/claims';
    navigate(fullWidth && runId ? `${path}?full=1` : path, { replace });
  }, [navigate, full]);
  const setTab = (t) => { setTabState(t); try { sessionStorage.setItem(TAB_KEY, t); } catch { /* per-viewer convenience only */ } };

  // ── search and filter ─────────────────────────────────────────────────────
  // A week matches on its dates; a client, Medicaid ID or ICN on its lines.
  useEffect(() => {
    const term = safeTerm(search);
    if (term.length < 2) { setLineHits(null); setSearching(false); return undefined; }
    setSearching(true);
    const t = setTimeout(async () => {
      const like = `%${term}%`;
      const { data } = await supabase.from('claim_lines').select('billing_run_id')
        .or(`client_name.ilike.${like},medicaid_id.ilike.${like},icn.ilike.${like}`)
        .not('billing_run_id', 'is', null).limit(5000);
      setLineHits(new Set((data || []).map(r => r.billing_run_id)));
      setSearching(false);
    }, 300);
    return () => clearTimeout(t);
  }, [search]);

  const visibleRuns = useMemo(() => {
    const q = search.trim().toLowerCase();
    return runs.filter(r => {
      if (filter === 'paid' && r.status !== 'closed') return false;
      if (filter === 'look' && !needsALook(marks[r.id])) return false;
      if (!q) return true;
      const text = `${weekLabel(r)} ${r.date_from} ${r.date_to} ${r.gammis_batch_ref || ''} ${r.ra_reference || ''}`.toLowerCase();
      return text.includes(q) || (lineHits && lineHits.has(r.id));
    });
  }, [runs, filter, marks, search, lineHits]);

  // ── keyboard: ↑ ↓ between runs, F full width, Esc back to the list ────────
  useEffect(() => {
    const onKey = (e) => {
      if (e.metaKey || e.ctrlKey || e.altKey) return;
      if (e.target.closest?.('input, textarea, select, [contenteditable="true"]')) return;
      if (document.querySelector('.modal-overlay')) return;
      if ((e.key === 'ArrowDown' || e.key === 'ArrowUp') && visibleRuns.length) {
        e.preventDefault();
        const i = visibleRuns.findIndex(r => r.id === selected?.id);
        const next = visibleRuns[Math.min(visibleRuns.length - 1, Math.max(0, i + (e.key === 'ArrowDown' ? 1 : -1)))];
        if (next && next.id !== selected?.id) go(next.id, { replace: true });
      } else if ((e.key === 'f' || e.key === 'F') && selected && !isMobile) {
        go(selected.id, { fullWidth: true });
      } else if (e.key === 'Escape' && full && selected) {
        go(selected.id, { fullWidth: false });
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [visibleRuns, selected, full, isMobile, go]);

  // Open a remittance the worker already fetched, at the review step. The same
  // modal, the same matching, the same posting — only the way in is different,
  // because the bytes are already here rather than on somebody's desktop.
  const openStoredRemittance = (remit, run) => {
    const parsed = parse835(remit.raw || '');
    // Said out loud: the page used to set this error inside the closed
    // dialog, where nobody could see it.
    if (!parsed?.claims?.length) {
      alert('This remittance could not be read. Download it and check what arrived.');
      return;
    }
    setEraModal({ runId: run.id, dateFrom: run.date_from, dateTo: run.date_to, storedId: remit.id, parsed });
  };

  // ── ICN entry ─────────────────────────────────────────────────────────────
  const openIcn = (run) => { setIcnModal({ runId: run.id, current: run.gammis_batch_ref || '', payer: run.payer }); setIcnValue(run.gammis_batch_ref || ''); };
  const saveICN = async () => {
    if (!icnModal || !icnValue.trim()) return;
    setIcnSaving(true);
    try {
      await recordClaimReference(supabase, { runId: icnModal.runId, reference: icnValue.trim(), now: new Date().toISOString() });
    } catch (err) {
      alert(err.message);
      setIcnSaving(false);
      return;
    }
    setIcnModal(null); setIcnValue(''); setIcnSaving(false);
    loadRuns();
    if (selected?.id === icnModal.runId) loadLines(icnModal.runId);
  };

  // ── one line, from its side panel (each throws; the panel shows why) ──────
  const changeLineStatus = async (line, status) => {
    const upd = await setLineStatus(supabase, {
      lineId: line.id, status, today: format(new Date(), 'yyyy-MM-dd'), now: new Date().toISOString(),
    });
    setLines(prev => prev.map(l => l.id === line.id ? { ...l, ...upd } : l));
    setOpenLine(l => (l && l.id === line.id ? { ...l, ...upd } : l));
  };
  const saveLine = async (line, form) => {
    // A payment corrected by hand moves the run's total with it.
    const upd = await writeLineEdit(supabase, {
      lineId: line.id, runId: line.billing_run_id || null, form, now: new Date().toISOString(),
    });
    setLines(prev => prev.map(l => l.id === line.id ? { ...l, ...upd } : l));
    setOpenLine(l => (l && l.id === line.id ? { ...l, ...upd } : l));
    loadRuns();
  };
  const removeOpenLine = async (line) => {
    const remaining = lines.filter(l => l.id !== line.id);
    await removeLine(supabase, { lineId: line.id, runId: line.billing_run_id, remaining });
    setLines(remaining);
    setOpenLine(null);
    loadRuns();
  };

  // ── Delete entire run ─────────────────────────────────────────────────────
  const deleteRun = async () => {
    if (!deleteConfirm) return;
    setDeleting(true); setDeleteError('');
    try {
      // A run that was filed or paid stays; the dialog says why.
      const out = await removeRun(supabase, deleteConfirm.id);
      if (!out.deleted) { setDeleteError(out.reason); return; }
      const wasSelected = selected?.id === deleteConfirm.id;
      setDeleteConfirm(null);
      await loadRuns();
      if (wasSelected) go(null, { replace: true });
    } catch (err) {
      setDeleteError(err.message);
    } finally {
      setDeleting(false);
    }
  };

  const toggleSort = (key) => {
    if (sortKey === key) setSortAsc(p => !p);
    else { setSortKey(key); setSortAsc(true); }
  };
  const openERA = (run) => setEraModal({ runId: run.id, dateFrom: run.date_from, dateTo: run.date_to });
  const afterRunChanged = (runId) => {
    loadRuns();
    if (selected?.id === runId) { loadLines(runId); recheck(selected); }
  };

  // ── Render ────────────────────────────────────────────────────────────────
  const showList = !full && (!isMobile || !runIdParam);
  const showPanel = !isMobile || !!runIdParam;

  return (
    <div>
      <input ref={ackFileRef} type="file" accept=".dat,.txt,.edi,.997,.999,.824,text/plain"
             style={{ display: 'none' }} onChange={handleAckFile} />

      {loading && !runs.length ? (
        <div className="empty-state"><div className="loading-spinner" style={{ margin: '0 auto' }} /></div>
      ) : !runs.length ? (
        <div className="empty-state">
          <p>No billing runs yet.</p>
          <button className="btn btn-primary" onClick={() => setShowWizard(true)}>+ New run</button>
        </div>
      ) : (
        <div className={`runs-split${full ? ' full' : ''}${isMobile ? ' mobile' : ''}`}>
          {showList && (
            <RunList
              runs={visibleRuns} total={runs.length} marks={marks} selectedId={isMobile ? null : selected?.id}
              onSelect={(r) => go(r.id)} search={search} onSearch={setSearch} searching={searching}
              filter={filter} onFilter={setFilter} onNew={() => setShowWizard(true)} onImport={() => setBackfillOpen(true)}
            />
          )}
          {showPanel && (selected ? (
            <RunPanel
              run={selected} lines={lines} linesLoading={linesLoading} mark={marks[selected.id]}
              tab={tab} onTab={setTab} full={full} isMobile={isMobile}
              onFull={() => go(selected.id, { fullWidth: true })} onShowList={() => go(selected.id, { fullWidth: false })}
              onBack={() => go(null)}
              onCsv={() => exportGAMMIS(selected.id, selected.date_from, selected.date_to)}
              on837P={() => exportEDI(selected.id, selected.date_from, selected.date_to)} submitting={submitting === selected.id}
              onResync={() => setResyncRun(selected)} onEnterRef={() => openIcn(selected)}
              onPostRemit={() => openERA(selected)} onDelete={() => setDeleteConfirm(selected)} onRecheck={() => recheck(selected)}
              exportProblems={exportProblems} onDismissProblems={() => setExportProblems(null)}
              identity={identity} settings={settings} transportConfigured={transportConfigured} transport={transport}
              transfers={transfers} anySubmissions={Object.values(submissions).some(list => list?.length)}
              onClaimStatus={askClaimStatus} onRefresh={refreshFromGammis}
              subs={submissions[selected.id] || []}
              onDownloadFile={(sub) => downloadEdi(sub.edi_content, sub.file_name)}
              onSendFile={requestSend} onMarkSent={markSent}
              onAcknowledgement={(sub) => { chooseAckFor(sub); ackFileRef.current?.click(); }}
              pendingRemits={pendingRemits} onOpenStoredRemittance={openStoredRemittance} eobIndex={eobIndex}
              sortKey={sortKey} sortAsc={sortAsc} onSort={toggleSort} onOpenLine={setOpenLine}
            />
          ) : (
            <div className="runs-empty">{runIdParam && !loading ? 'That run no longer exists.' : 'Choose a run.'}</div>
          ))}
        </div>
      )}

      {openLine && selected && (
        <LinePanel
          line={openLine} run={selected} onClose={() => setOpenLine(null)}
          onSave={(form) => saveLine(openLine, form)}
          onChangeStatus={(status) => changeLineStatus(openLine, status)}
          onRemove={() => removeOpenLine(openLine)}
        />
      )}

      {showWizard && (
        <NewRunWizard
          profile={profile}
          onClose={() => setShowWizard(false)}
          onCreated={loadRuns}
          onExportCsv={(run) => exportGAMMIS(run.id, run.date_from, run.date_to)}
          onExport837P={(run) => exportEDI(run.id, run.date_from, run.date_to)}
          onEnterReference={(run) => setIcnModal({ runId: run.id, current: '', payer: run.payer })}
        />
      )}

      {resyncRun && (
        <ResyncModal run={resyncRun} onClose={() => setResyncRun(null)} onApplied={afterRunChanged} />
      )}

      {backfillOpen && (
        <BackfillModal onClose={() => setBackfillOpen(false)} onImported={loadRuns} />
      )}

      {deleteConfirm && (
        <DeleteRunDialog run={deleteConfirm} deleting={deleting} error={deleteError}
          onCancel={() => { setDeleteConfirm(null); setDeleteError(''); }} onDelete={deleteRun} />
      )}

      {sendConfirm && (
        <SendToGammisDialog sub={sendConfirm} run={runs.find(r => r.id === sendConfirm.billing_run_id)}
          sending={!!transport?.busy} onCancel={() => setSendConfirm(null)} onConfirm={confirmSend} />
      )}

      {icnModal && (
        <IcnDialog modal={icnModal} value={icnValue} onChange={setIcnValue} saving={icnSaving}
          onCancel={() => setIcnModal(null)} onSave={saveICN} />
      )}

      {eraModal && (
        <RemittanceModal
          initial={eraModal}
          profile={profile}
          onClose={() => setEraModal(null)}
          onStoredPosted={loadPendingRemittances}
          onPosted={({ runId }) => afterRunChanged(runId)}
        />
      )}
    </div>
  );
}
